import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useParams, Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Percent, Plus, Search, Lightbulb, Timer, Megaphone, ChevronDown,
  Bookmark, Share2, SquareArrowOutUpRight, Tag,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Offer, Product, SalesDiscount } from "@shared/schema";
import { optimizedImg } from "@/lib/img";
import { personaNameTier } from "@/components/persona-build-card";
import { SaleBuildCard, SaleBuildFailedCard } from "@/components/sale-build-card";
import { computeSaleStrength, isSaleStrengthReady, type SaleStrengthInput } from "@shared/sale-strength";
import { SALE_TYPES, URGENCY_MECHANISMS } from "@/pages/sales-discount-form";

// ── Shared design tokens (persona/offering-card design language) ────────────
// The sale card deliberately reuses the offering card's look (which itself
// mirrors the persona card): same serif stack, same warm near-black band,
// same fixed-height uniform-grid treatment. Keep all three in lockstep.

const SERIF = { fontFamily: `'Iowan Old Style', 'Palatino Linotype', Georgia, 'Times New Roman', serif` };
const BAND = "#231c16";

// Every sale card renders to one uniform height (see personas.tsx for the
// full rationale): the hero band is fixed, and the chip / quote bands reserve
// their space whether or not the sale has the data.
const CARD_MIN_H = "min-h-[480px]";

// Title type classes keyed by personaNameTier — the full sale name is always
// shown; longer names step down in size and wrap instead.
const TITLE_TIER_CLASS: Record<ReturnType<typeof personaNameTier>, string> = {
  base: "text-[22px] md:text-[24px] leading-[24px] md:leading-[26px]",
  medium: "text-[17px] md:text-[18px] leading-[21px] md:leading-[22px]",
  small: "text-[14px] md:text-[15px] leading-[18px] md:leading-[19px]",
  xs: "text-[12.5px] md:text-[13px] leading-[16px] md:leading-[17px]",
};

const SALE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  SALE_TYPES.map(t => [t.value, t.label]),
);
const URGENCY_LABELS: Record<string, string> = Object.fromEntries(
  URGENCY_MECHANISMS.map(u => [u.value, u.label]),
);

function getInitials(name: string): string {
  return name.split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function trimmed(v: string | null | undefined): string | null {
  const t = (v ?? "").trim();
  return t || null;
}

// "Ends Jun 21, 2026" (or null when no expiration is set / unparseable).
function expirationText(sale: SalesDiscount): string | null {
  if (!sale.expirationDate) return null;
  const d = new Date(sale.expirationDate as unknown as string);
  if (isNaN(d.getTime())) return null;
  return `Ends ${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
}

// Urgency mechanism label — the 'no_urgency' default is not a mechanism.
function urgencyLabel(sale: SalesDiscount): string | null {
  const m = trimmed(sale.urgencyMechanism);
  if (!m || m === "no_urgency") return null;
  return URGENCY_LABELS[m] || m.replace(/_/g, " ");
}

function saleStrength(s: SalesDiscount, hasHeroPhoto: boolean): number {
  return computeSaleStrength({ ...(s as SaleStrengthInput), hasHeroPhoto });
}

// Compact side-by-side insight chip (persona TraitChip design): icon + label
// row, then ~2 short lines of condensed text. Height-locked — the text slot
// always reserves two lines so the chip band lines up across every card.
// Missing values render a quiet placeholder rather than dropping the chip.
function InsightChip({ label, text, icon: Icon, tint, testId }: {
  label: string;
  text: string | null;
  icon: typeof Tag;
  tint: { icon: string; iconBg: string; label: string };
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border/60 bg-card/60 px-2 py-2.5 min-w-0" data-testid={testId}>
      <div className="flex items-center gap-1 min-w-0">
        <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border ${tint.iconBg}`}>
          <Icon className={`h-3 w-3 ${tint.icon}`} />
        </div>
        <p className={`text-[8.5px] leading-[13px] font-bold uppercase tracking-[0.08em] truncate ${tint.label}`}>{label}</p>
      </div>
      <p className={`text-[11px] leading-[15px] h-[30px] line-clamp-2 ${text ? "text-muted-foreground" : "text-muted-foreground/45 italic"}`}>
        {text || "Not captured yet"}
      </p>
    </div>
  );
}

function StrengthGauge({ pct }: { pct: number }) {
  const C = 2 * Math.PI * 16; // r=16 in a 40x40 viewBox
  return (
    <div className="relative w-[44px] h-[44px] flex-shrink-0" data-testid="gauge-sale-strength">
      <svg viewBox="0 0 40 40" className="w-full h-full -rotate-90">
        <circle cx="20" cy="20" r="16" fill="none" strokeWidth="3.5" className="stroke-muted/70" />
        <circle
          cx="20" cy="20" r="16" fill="none" strokeWidth="3.5" strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * C} ${C}`}
          className={isSaleStrengthReady(pct) ? "stroke-emerald-500" : "stroke-emerald-600/80"}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold tabular-nums">{pct}</span>
    </div>
  );
}

function SaleCard({ sale, brandId, heroPhoto, index }: {
  sale: SalesDiscount;
  brandId: string;
  heroPhoto: string | null;
  index: number;
}) {
  const [termsOpen, setTermsOpen] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  // Build-with-AI drafts: while the persist-first generation runs the row
  // renders as an animated build card; a failed build gets retry/discard.
  // The branch lives INSIDE this always-mounted component so the one-time
  // reveal below can see the building→done transition.
  const isFailed = sale.buildStatus === "failed";
  const isBuilding = sale.buildStatus === "building" && !isFailed;

  // One-time reveal: when the build this mounted card was watching resolves
  // successfully, the finished sale card animates in. Sales that mount
  // already-done never get the animation (the ref starts false).
  const wasBuildingRef = useRef(isBuilding);
  const [reveal, setReveal] = useState(false);
  useEffect(() => {
    if (wasBuildingRef.current && !isBuilding && !isFailed) {
      wasBuildingRef.current = false;
      setReveal(true);
      const t = window.setTimeout(() => setReveal(false), 900);
      return () => window.clearTimeout(t);
    }
    wasBuildingRef.current = isBuilding;
  }, [isBuilding, isFailed]);

  const pct = saleStrength(sale, !!heroPhoto);
  const ready = isSaleStrengthReady(pct);
  const isActive = sale.status === "active";

  // Band image chain: generated AI hero (product-based, lands in the
  // background) → real-photo chain (heroPhoto prop) → initials placeholder.
  // The strength gauge deliberately keeps scoring the REAL-photo chain so an
  // auto-generated hero doesn't inflate sale strength. Kept in lockstep with
  // the offering card.
  const displayHero = sale.heroImageUrl || heroPhoto;

  const typeLabel = sale.saleType
    ? SALE_TYPE_LABELS[sale.saleType] || sale.saleType.replace(/_/g, " ")
    : null;
  const discount = trimmed(sale.discountValue);

  // Meta line: discount value • urgency mechanism / expiration.
  const urgency = urgencyLabel(sale);
  const expires = expirationText(sale);
  const metaParts = [discount, urgency || expires].filter(Boolean) as string[];

  const description = trimmed(sale.promotionSummary);

  // Insight chips from the structured sale fields. The Urgency chip combines
  // the mechanism with the expiration date when both exist.
  const whyThisSale = trimmed(sale.whyThisSaleExists);
  const urgencyChip = urgency && expires ? `${urgency} — ${expires.toLowerCase()}` : urgency || expires;
  const cta = trimmed(sale.ctaLanguage);

  // Quote strip: the customer-facing copy is the serif italic quote.
  const quote = trimmed(sale.customerFacingCopy);
  // Renders only once the one-shot generation has landed (missing/failed
  // sketches simply collapse the slot, no broken image or empty gap).
  const sketch = sale.sketchImageUrl || null;
  const promoCode = trimmed(sale.promoCode);
  const terms = trimmed(sale.termsConditions);

  const nameTier = personaNameTier(sale.name);

  if (isFailed) {
    return <SaleBuildFailedCard sale={sale} brandId={brandId} index={index} />;
  }
  if (isBuilding) {
    return <SaleBuildCard sale={sale} index={index} />;
  }

  return (
    <Link href={`/brands/${brandId}/sales-discounts/${sale.id}`}>
      <Card
        className={`persona-card group relative cursor-pointer h-full ${CARD_MIN_H} overflow-hidden rounded-2xl border-border/40 hover:border-border/80 hover:shadow-lg transition-all duration-200 ${reveal ? "persona-reveal" : ""}`}
        style={{ animationDelay: `${index * 80}ms` }}
        data-testid={`card-sale-${sale.id}`}
      >
        <CardContent className="p-0 flex flex-col h-full">
          {/* ── Dark cinematic hero band ─────────────────────────────── */}
          <div className="relative overflow-hidden h-[200px] md:h-[210px] flex-shrink-0 flex" style={{ backgroundColor: BAND }}>
            {displayHero ? (
              <div className="absolute inset-0 md:inset-y-0 md:left-auto md:right-0 md:w-[58%]" data-testid={`img-hero-${sale.id}`}>
                <img
                  src={optimizedImg(displayHero, 1280)}
                  alt={sale.name}
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                {/* Blend into the dark band. Overlay extends a few px past the
                    image's left edge so subpixel rounding at fractional
                    percentage widths can never leave an uncovered hairline. */}
                <div
                  className="absolute inset-y-0 -left-1 right-0 pointer-events-none"
                  style={{ background: `linear-gradient(to right, ${BAND} 0%, ${BAND}cc 18%, ${BAND}33 55%, transparent 100%)` }}
                />
                <div
                  className="absolute inset-x-0 bottom-0 h-16 pointer-events-none md:hidden"
                  style={{ background: `linear-gradient(to top, ${BAND}e6, transparent)` }}
                />
              </div>
            ) : (
              <div className="absolute inset-y-0 right-0 w-[50%] flex items-center justify-center bg-gradient-to-l from-white/[0.07] via-white/[0.03] to-transparent pointer-events-none">
                <span className="text-6xl font-bold text-white/10 tracking-wide">{getInitials(sale.name)}</span>
              </div>
            )}

            {/* Fixed-height band, flexible name slot: names are never
                truncated — long names step down in type size and wrap. Meta
                stays one line and description two, so the band never grows. */}
            <div className="relative z-10 flex flex-col justify-center gap-2 p-5 w-full md:max-w-[52%] overflow-hidden">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-2.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-[0.18em] text-white/75 backdrop-blur-sm">
                  <Percent className="h-3 w-3" />Sale
                </span>
                {typeLabel && (
                  <span className="inline-flex w-fit items-center rounded-full bg-white/10 border border-white/15 px-2.5 py-0.5 text-[8.5px] font-semibold capitalize tracking-[0.06em] text-white/70 backdrop-blur-sm" data-testid={`badge-type-${sale.id}`}>
                    {typeLabel}
                  </span>
                )}
                {discount && (
                  <span className="inline-flex w-fit items-center rounded-full bg-white/10 border border-white/15 px-2.5 py-0.5 text-[8.5px] font-semibold tracking-[0.06em] text-white/70 backdrop-blur-sm" data-testid={`badge-discount-${sale.id}`}>
                    {discount}
                  </span>
                )}
              </div>
              <h3
                className={`text-[#f5efe4] tracking-tight ${TITLE_TIER_CLASS[nameTier]}`}
                style={SERIF}
                data-testid={`text-name-${sale.id}`}
              >
                {sale.name}
              </h3>
              <p className="text-[11.5px] leading-[16px] h-[16px] truncate text-white/75 font-medium" data-testid={`text-meta-${sale.id}`}>
                {metaParts.map((part, i) => (
                  <span key={i}>
                    {i > 0 && <span className="mx-1.5 text-white/40">•</span>}
                    {part}
                  </span>
                ))}
              </p>
              <p className="text-[12px] text-white/55 leading-[17px] h-[34px] line-clamp-2 mt-0.5" data-testid={`text-description-${sale.id}`}>
                {description || "No summary yet — click to add sale details"}
              </p>
            </div>
          </div>

          {/* ── Why This Sale / Urgency / CTA chips ──────────────────── */}
          {/* Three equal-width chips in one horizontal row. All three slots
              always render (quiet placeholder when a field is missing) so the
              chip band is the same height on every card. */}
          <div className="grid grid-cols-3 gap-2 px-4 pt-4">
            <InsightChip
              label="Why This Sale" text={whyThisSale} icon={Lightbulb} testId={`chip-why-${sale.id}`}
              tint={{ icon: "text-rose-500", iconBg: "bg-rose-500/10 border-rose-500/15", label: "text-rose-500/90" }}
            />
            <InsightChip
              label="Urgency" text={urgencyChip} icon={Timer} testId={`chip-urgency-${sale.id}`}
              tint={{ icon: "text-orange-500", iconBg: "bg-orange-500/10 border-orange-500/15", label: "text-orange-500/90" }}
            />
            <InsightChip
              label="CTA" text={cta} icon={Megaphone} testId={`chip-cta-${sale.id}`}
              tint={{ icon: "text-emerald-500", iconBg: "bg-emerald-500/10 border-emerald-500/15", label: "text-emerald-500/90" }}
            />
          </div>

          {/* ── Quote + promo code strip ─────────────────────────────── */}
          {/* Matches the offering card: oversized quote mark + italic serif
              customer-facing copy on the left, a faint hand-drawn line-art
              sketch in the middle, and the PROMO CODE block + chevron on the
              right. Collapsed, both text blocks reserve exactly three lines
              so the strip is the same height on every card; the sketch slot
              only renders when the illustration exists (the text blocks flex
              wider in its absence — no broken image, no empty gap).
              Expanding reveals the terms & conditions and only grows this
              card. */}
          <div className="mx-4 mt-3 rounded-xl bg-amber-500/[0.05] border border-amber-500/10 px-4 py-3.5 flex items-center gap-3">
            <div className="flex-[1.2] min-w-0 flex gap-2 self-center">
              <span className="text-[38px] leading-[30px] text-emerald-800/75 select-none flex-shrink-0" style={SERIF}>&ldquo;</span>
              <p
                className={`italic text-[13.5px] leading-[18px] self-center ${termsOpen ? "min-h-[54px]" : "line-clamp-3 max-h-[54px]"} ${quote ? "text-foreground/85" : "text-muted-foreground/45"}`}
                style={SERIF}
                data-testid={`text-quote-${sale.id}`}
              >
                {quote || "No customer-facing copy captured yet"}
              </p>
            </div>
            {sketch && (
              /* The slot stretches to the strip's text-driven height and the
                 (larger) artwork is absolutely centered inside it, so the
                 sketch never changes the strip height — cards with and
                 without a sketch stay identical. Full-strength multiply (no
                 partial opacity) makes the cream PNG background vanish into
                 the tan strip, and a radial mask fades the artwork's edges so
                 nothing terminates in a hard rectangle (same in dark mode,
                 where invert+screen does the blending). */
              <div className="relative hidden sm:block w-[120px] flex-shrink-0 self-stretch" data-testid={`img-sketch-${sale.id}`}>
                <img
                  src={optimizedImg(sketch, 480)}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-[92px] w-full object-contain mix-blend-multiply dark:invert dark:mix-blend-screen dark:opacity-70 pointer-events-none select-none"
                  style={{
                    WebkitMaskImage: "radial-gradient(ellipse farthest-side at center, black 55%, transparent 100%)",
                    maskImage: "radial-gradient(ellipse farthest-side at center, black 55%, transparent 100%)",
                  }}
                />
              </div>
            )}
            <div className="flex-1 min-w-0 flex items-start gap-2 border-l border-amber-500/10 pl-3">
              <div className="flex-1 min-w-0">
                <p className="text-[9.5px] leading-[13px] font-bold uppercase tracking-[0.15em] text-amber-600/90 mb-1">Promo Code</p>
                {promoCode ? (
                  <p
                    className={`text-[11.5px] leading-[16px] font-mono font-semibold tracking-[0.03em] text-foreground/85 ${termsOpen ? "min-h-[48px]" : "h-[48px]"} break-all`}
                    data-testid={`text-promo-code-${sale.id}`}
                  >
                    {promoCode}
                  </p>
                ) : (
                  <p
                    className={`text-[11px] leading-[16px] text-muted-foreground/45 italic ${termsOpen ? "min-h-[48px]" : "line-clamp-3 h-[48px]"}`}
                    data-testid={`text-promo-code-${sale.id}`}
                  >
                    No promo code — sale applies automatically
                  </p>
                )}
                {termsOpen && terms && (
                  <p className="text-[11px] leading-[16px] text-muted-foreground mt-2" data-testid={`text-terms-${sale.id}`}>
                    <span className="font-semibold text-emerald-600/90">Terms: </span>
                    {terms}
                  </p>
                )}
              </div>
              {terms && (
                <button
                  className="w-7 h-7 rounded-full border border-border/60 bg-card flex items-center justify-center flex-shrink-0 hover:bg-muted/50 transition-colors self-center"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTermsOpen(o => !o); }}
                  data-testid={`button-expand-terms-${sale.id}`}
                  aria-label={termsOpen ? "Collapse" : "Expand"}
                >
                  <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${termsOpen ? "rotate-180" : ""}`} />
                </button>
              )}
            </div>
          </div>

          {/* ── Footer ───────────────────────────────────────────────── */}
          <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-3 px-4 py-3.5">
            <div className="flex items-center gap-2.5 min-w-0">
              <StrengthGauge pct={pct} />
              <div className="min-w-0">
                <p className="text-[12px] font-semibold leading-tight whitespace-nowrap">Sale Strength</p>
                <p className="text-[10.5px] text-muted-foreground leading-tight mt-0.5 truncate">
                  {ready ? "Strong promotion. Ready to run." : "Add more detail to strengthen."}
                </p>
                {/* Slot is always reserved so the footer is the same height
                    whether or not the sale is complete. */}
                <div className="h-5 mt-1">
                  {ready && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/15 px-2 py-0.5 text-[9px] font-semibold text-emerald-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Sale Ready
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <Button
                className="h-9 rounded-lg px-3.5 gap-1.5 text-[13px] bg-emerald-700 hover:bg-emerald-600 text-white shadow-sm"
                data-testid={`button-open-sale-${sale.id}`}
              >
                <SquareArrowOutUpRight className="h-3.5 w-3.5" />Open
              </Button>
              <div className="flex items-center gap-1.5" onClick={e => e.preventDefault()}>
                {/* Sharing has no sale backend yet — the control renders to
                    match the offering card but stays disabled until it does. */}
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full w-8 h-8"
                  disabled
                  title="Sharing isn't available for sales yet"
                  data-testid={`button-share-${sale.id}`}
                  aria-label="Share sale"
                >
                  <Share2 className="h-4 w-4 text-muted-foreground" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full w-8 h-8"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBookmarked(b => !b); }}
                  data-testid={`button-bookmark-${sale.id}`}
                  aria-label="Bookmark sale"
                >
                  <Bookmark className={`h-4 w-4 ${bookmarked ? "fill-current text-emerald-500" : "text-muted-foreground"}`} />
                </Button>
              </div>
              <Badge
                className={`text-[9px] font-semibold uppercase tracking-widest rounded-full px-3 h-6 border inline-flex items-center gap-1.5 ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                    : "bg-amber-500/10 text-amber-500 border-amber-500/20"
                }`}
                data-testid={`badge-status-${sale.id}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400" : "bg-amber-500"}`} />
                {sale.status || "draft"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function SalesDiscountsPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [search, setSearch] = useState("");

  // The grid is the ONE consumer that opts into Build-with-AI drafts
  // (?includeDrafts=1) so it can render animated build cards; every other
  // sales consumer gets only complete rows from the default endpoint. The
  // queryKey keeps the plain endpoint string as its first segment so the
  // app-wide invalidations of [`/api/brands/:id/sales-discounts`] still match
  // by prefix, while the extra segment keeps this cache entry separate from
  // the drafts-free one other pages share.
  const { data: sales, isLoading } = useQuery<SalesDiscount[]>({
    queryKey: [`/api/brands/${brandId}/sales-discounts`, { includeDrafts: true }],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/brands/${brandId}/sales-discounts?includeDrafts=1`);
      return res.json();
    },
    enabled: !!brandId,
    // Poll fast while any Build-with-AI generation is running (persona-grid
    // style, so the build card reveals the finished sale without a manual
    // refresh). buildStatus now stays 'building' through the AI hero phase,
    // so this fast poll also spans hero generation and the reveal fires with
    // the photo already in place. Otherwise: sketches land in the background
    // (claimed generations within seconds, never-attempted ones when the
    // backfill sweep picks them up), so keep a gentle poll going until every
    // sale's sketch is ready or terminally failed — then stop entirely. Build
    // drafts are excluded from the imagery checks: the hero runs INSIDE the
    // build and the sketch pipeline only starts once the build completes.
    refetchInterval: (query) => {
      const list = query.state.data;
      if (!list) return false;
      if (list.some(s => s.buildStatus === "building")) return 2500;
      const settled = list.filter(s => !s.buildStatus);
      const generating = (url: unknown, status: unknown) => !url && status === "generating";
      const pending = (url: unknown, status: unknown) => !url && status !== "failed";
      if (settled.some(s =>
        generating(s.sketchImageUrl, s.sketchImageStatus) ||
        generating(s.heroImageUrl, s.heroImageStatus),
      )) return 4000;
      if (settled.some(s =>
        pending(s.sketchImageUrl, s.sketchImageStatus) ||
        pending(s.heroImageUrl, s.heroImageStatus),
      )) return 15000;
      return false;
    },
  });

  // Products and offers back the hero-photo fallback chain (primary product
  // photo → first appliesTo product photo → primary offer's photo chain →
  // photo-less dark band).
  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: offers } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
  });

  const productPhotoById = useMemo(() => {
    const map: Record<string, string> = {};
    products?.forEach(p => {
      const media = (p.mediaJson as unknown[] | null) || [];
      const refs = (p.adgenImageRefs as unknown[] | null) || [];
      const photo = [...media, ...refs].find((u): u is string => typeof u === "string" && !!u.trim());
      if (photo) map[p.id] = photo;
    });
    return map;
  }, [products]);

  // An offer's photo chain mirrors the offerings grid: its own reference
  // images first, then the first included product's photo.
  const offerPhotoById = useMemo(() => {
    const map: Record<string, string> = {};
    offers?.forEach(o => {
      const refs = (o.adgenImageRefs as unknown[] | null) || [];
      const ownRef = refs.find((u): u is string => typeof u === "string" && !!u.trim());
      if (ownRef) {
        map[o.id] = ownRef;
        return;
      }
      const included = Array.isArray(o.includedProducts) ? (o.includedProducts as unknown[]) : [];
      for (const pid of included) {
        if (typeof pid === "string" && productPhotoById[pid]) {
          map[o.id] = productPhotoById[pid];
          return;
        }
      }
    });
    return map;
  }, [offers, productPhotoById]);

  const heroPhotoFor = (sale: SalesDiscount): string | null => {
    if (sale.primaryProductId && productPhotoById[sale.primaryProductId]) {
      return productPhotoById[sale.primaryProductId];
    }
    const appliesTo = sale.appliesTo as { productIds?: unknown } | null;
    const productIds = Array.isArray(appliesTo?.productIds) ? appliesTo.productIds : [];
    for (const pid of productIds) {
      if (typeof pid === "string" && productPhotoById[pid]) return productPhotoById[pid];
    }
    if (sale.primaryOfferId && offerPhotoById[sale.primaryOfferId]) {
      return offerPhotoById[sale.primaryOfferId];
    }
    return null;
  };

  const filteredSales = useMemo(() => {
    let result = sales || [];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(s =>
        s.name.toLowerCase().includes(q) ||
        (s.promotionSummary || "").toLowerCase().includes(q) ||
        (s.customerFacingCopy || "").toLowerCase().includes(q) ||
        (s.promoCode || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [sales, search]);

  if (isLoading) {
    return (
      <div className="p-8 max-w-6xl mx-auto space-y-6">
        <Skeleton className="h-32 rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map(i => <Skeleton key={i} className="h-[480px] rounded-2xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/10 px-8 py-8 fade-in-up">
        <div className="absolute top-0 right-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-1/3 w-48 h-48 bg-violet-500/5 rounded-full blur-3xl translate-y-1/2" />
        <div className="relative flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center border border-primary/20">
                <Percent className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight" data-testid="text-sales-discounts-title">Sales & Discounts</h1>
                <p className="text-sm text-muted-foreground">Reusable promotions you can attach to products, offers, and Ads Express campaigns</p>
              </div>
            </div>
          </div>
          <Link href={`/brands/${brandId}/sales-discounts/new`}>
            <Button className="rounded-full gap-2 px-5 shadow-lg shadow-primary/20" data-testid="button-create-sales-discounts">
              <Plus className="h-4 w-4" />New Sale
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 fade-in-up-delay-1">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sales by name, copy, or promo code..."
            className="pl-9 h-10 bg-card/50"
            data-testid="input-search-sales-discounts"
          />
        </div>
      </div>

      {filteredSales.length === 0 ? (
        <div className="text-center py-20 fade-in-up-delay-2">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
            <Percent className="h-8 w-8 text-primary/50" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1.5">
            {sales?.length === 0 ? "No sales yet" : "No matching sales"}
          </h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
            {sales?.length === 0 ? "Create one to start running promotions" : "Try adjusting your search terms"}
          </p>
          {sales?.length === 0 && (
            <Link href={`/brands/${brandId}/sales-discounts/new`}>
              <Button className="rounded-full px-6" data-testid="button-create-first-sale">
                <Plus className="h-4 w-4 mr-1.5" />Create Sale
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 fade-in-up-delay-2">
          {filteredSales.map((sale, i) => (
            <SaleCard
              key={sale.id}
              sale={sale}
              brandId={brandId!}
              heroPhoto={heroPhotoFor(sale)}
              index={i}
            />
          ))}
        </div>
      )}
    </div>
  );
}
